import { FormEvent } from 'react';
import { useTranslation } from '@/i18n';
import { formatInr } from './depositOptions';

type Props = {
  balance: number;
  amount: string;
  onAmountChange: (value: string) => void;
  /** Lowest minimum / highest maximum of the payment accounts, once they've loaded. */
  range: { min?: number; max?: number };
  onNext: () => void;
};

/** Step 1: show the available balance and take the number of coins to deposit. */
const DepositAmountStep = ({ balance, amount, onAmountChange, range, onNext }: Props) => {
  const { t } = useTranslation();
  const value = Number(amount);
  const outOfRange =
    value > 0 && ((range.min && value < range.min) || (range.max && value > range.max));
  const canContinue = value > 0 && !outOfRange;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (canContinue) onNext();
  };

  const hint =
    range.min && range.max
      ? t('deposit.range', { min: formatInr(range.min), max: formatInr(range.max) })
      : range.min
        ? t('deposit.rangeFrom', { min: formatInr(range.min) })
        : null;

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="rounded-xl border border-gold/30 bg-gold/10 px-4 py-5 text-center">
        <p className="text-sm text-shell-muted">{t('deposit.availableBalance')}</p>
        <p className="mt-1 text-2xl font-bold text-gold">
          {balance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </p>
      </div>

      <div>
        <label htmlFor="deposit-coins" className="mb-2 block text-base font-medium">
          {t('deposit.coins')}
        </label>
        <input
          id="deposit-coins"
          value={amount}
          onChange={(event) => onAmountChange(event.target.value.replace(/\D/g, '').slice(0, 9))}
          inputMode="numeric"
          autoComplete="off"
          autoFocus
          placeholder={t('deposit.enterCoins')}
          className="h-12 w-full rounded-xl border border-shell-border bg-shell-raised px-4 text-base text-shell-fg outline-none transition placeholder:text-shell-muted focus:border-gold"
        />
        {hint && (
          <p className={`mt-2 text-sm ${outOfRange ? 'text-danger-red' : 'text-shell-muted'}`}>
            {hint}
          </p>
        )}
      </div>

      <button
        type="submit"
        disabled={!canContinue}
        className="h-12 w-full rounded-xl bg-gold text-base font-semibold text-gold-foreground transition hover:bg-gold-hover disabled:cursor-not-allowed disabled:opacity-50"
      >
        {t('deposit.makePayment')}
      </button>
    </form>
  );
};

export default DepositAmountStep;
